import { HistoryMode } from "src/clients/flow-api";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import { useWorkspaceContext } from "src/router/routerContextHooks";

/**
 * Hook to determine if Iceberg should be used for metrics endpoints
 *
 * Returns true if either:
 * - The workspace config `enable_dhv2_metrics` is enabled AND the history_mode
 *   writes to Iceberg (any mode except write_aurora_read_aurora), OR
 * - Falls back to the existing `useIsIcebergEnabled()` behavior
 *
 * Use this hook for metrics endpoints that support Iceberg reads:
 * - /metrics/api/v1/operational/summary
 * - /metrics/api/v1/decision_summary
 * - /metrics/api/v1/operational/latency
 * - /metrics/api/v1/operational/errors
 * - /metrics/api/v1/operational/decisions
 *
 */
export const useIsIcebergMetricsEnabled = (): boolean => {
  const { workspace } = useWorkspaceContext();
  const isIcebergEnabled = useIsIcebergEnabled();
  const historyMode = workspace.config?.history_mode;

  // enable_dhv2_metrics is only effective when the workspace writes to Iceberg.
  // AURORA_READ_AURORA is the only mode that doesn't write to Iceberg.
  if (workspace.config?.enable_dhv2_metrics) {
    return (
      historyMode !== undefined &&
      historyMode !== HistoryMode.AURORA_READ_AURORA
    );
  }

  // Fall back to existing iceberg behavior
  return isIcebergEnabled;
};
